import React, { useEffect, useState } from "react";
import UITransition from "../../ui/Transition";
import { CustomStyleProvider } from "../../utils/CustomStyleProvider";
import { Col, Row, Slider, TablePaginationConfig } from "antd";
import { UITitle } from "../../ui/Title";
import { BaseColor } from "../../../theme/maps/Colors";
import { ExceptionOutlined, FundOutlined, PrinterOutlined, RetweetOutlined, SettingOutlined } from "@ant-design/icons";
import { UICard } from "../../ui/Card";
import { UIText } from "../../ui/Text";
import { UIButton } from "../../ui/Button";
import { useDispatch, useSelector } from "react-redux";
import { selectorCustomStyleOptions } from "../../../redux-modules/selectors/selectorsUI";
import { actionCustomStyleOptionUpdateRequest } from "../../../redux-modules/actions/actionsUI";
import styled from "styled-components";
import { DrawerCreateTurniOccasionali } from "./drawer";
import { selectorPermissionList } from "../../../redux-modules/selectors/selectorsPermissions";
import { hasPermission } from "../../../utils/permissions/hasPermission";
import { TTemporaryShiftCreateRequest, TTemporaryShiftList } from "../../../redux-modules/declarations/maps/shifts";
import { selectorLocationsList } from "../../../redux-modules/selectors/selectorsLocations";
import apiClient from "../../../utils/apiClient";
import { API_ENDPOINTS } from "../../../constants/api";
import { showNotification } from "../../../utils/showNotification";
import { UITable } from "../../ui/Table";
import { eventsListColumns } from "./eventsListColumns";
import { useHistory } from "react-router";
import { Routes } from "../../../utils/Routes";

export const ViewTurniOccasionali = (): JSX.Element => {
  const dispatch = useDispatch();
  const history = useHistory();

  const [showCustomStyleOptions, setShowCustomStyleOptions] = useState(false);
  const customStyleOptions = useSelector(selectorCustomStyleOptions);
  const [zoomLevel, setZoomLevel] = useState(customStyleOptions["ShiftsListZoomLevel"]);

  const permissionList = useSelector(selectorPermissionList);
  const canAddTemporaryShift = hasPermission(permissionList, "TemporaryShift", "Create");

  const [showDrawerCreateTemporaryShift, setShowDrawerCreateTemporaryShift] = useState(false);
  const [isCreateLoading, setIsCreateLoading] = useState(false);

  const [eventsList, setEventsList] = useState<TTemporaryShiftList>();
  const [eventsListLoading, setEventsListLoading] = useState(false);

  useEffect(() => {
    if (customStyleOptions["ShiftsListZoomLevel"] !== zoomLevel) {
      dispatch(
        actionCustomStyleOptionUpdateRequest({
          ...customStyleOptions,
          ShiftsListZoomLevel: zoomLevel,
        }),
      );
    }
  }, [zoomLevel]);

  useEffect(() => {
    getEvents();
  }, []);
  
  const handleOnCloseDrawerCreateTurniOccasionali = () => {
    setShowDrawerCreateTemporaryShift(false)
  };

  async function getEvents(page? : number){
    setEventsListLoading(true)
    try{
      
      const response = await apiClient.get(API_ENDPOINTS.TEMPORARY_SHIFTS_SHOW + (page ? `?page=${page}`: ''))

      if(response.status == 200){
        const res = response.data;
        
        const result: TTemporaryShiftList = {
          data: res.data,
          pagination: {
            current: res.current_page,
            pageSize: res.per_page,
            lastPage: res.last_page,
            total: res.total
          }
        };

        setEventsList(result)
        
      }
    }catch(err){
      // Caso in cui err ha la response della chiamata perché l'api non ha restituito 200
      // @ts-ignore 
      if(err.response){
        // @ts-ignore
        const response: AxiosResponse = err.response;

        if(response.data){
          const info = response.data;
          showNotification("error", "Turno Occasionale", info.message);
        }
      }else{
        showNotification("error", "Turno Occasionale", "Si è verificato un imprevisto al caricamento dei turni, riprovare.");
      }
    }finally{
      setEventsListLoading(false)
    }
  }

  const onSaveDrawerCreateTurniOccasionali = async (info: TTemporaryShiftCreateRequest) => {
    setIsCreateLoading(true)
    try{
      const response = await apiClient.post(API_ENDPOINTS.TEMPORARY_SHIFTS, info)
    
      if(response.status == 200){
        showNotification("success", "Turno Occasionale", response.data.message); 
        setShowDrawerCreateTemporaryShift(false)

        // NOVITÀ: la creazione dell'evento ora è solo il primo step (nome/data/luogo).
        // Portiamo subito l'utente sul dettaglio per fargli aggiungere le sedi,
        // invece di lasciarlo sulla lista.
        const newEventId = response.data?.data?.id;

        if (newEventId) {
          history.push(Routes.turniOccasionaliDetail.replace(":id", newEventId));
        } else {
          getEvents()
        }
      }
    }catch(err){
      // Caso in cui err ha la response della chiamata perché l'api non ha restituito 200
      // @ts-ignore 
      if(err.response){
        // @ts-ignore
        const response: AxiosResponse = err.response;

        if(response.data){
          const info = response.data;
          showNotification("error", "Turno Occasionale", info.message);
        }
      }else{
        showNotification("error", "Turno Occasionale", "Si è verificato un imprevisto, riprovare.");
      }
    }finally{
      setIsCreateLoading(false)
    }
  };

  const handleTableChange = (pagination: TablePaginationConfig): void => {
    getEvents(pagination.current)
  };

  return (
    <>
      <UITransition>
        <CustomStyleProvider section="Shifts">
          <Row justify="space-between" align="middle" gutter={[16, 16]}>
            <Col style={{ display: "inline-flex", alignItems: "baseline" }}>
              <UITitle level={2} color={BaseColor}>
                Turni Occasionali
              </UITitle>
              <span
                style={{
                  fontSize: "18px",
                  color: BaseColor,
                  cursor: "pointer",
                  marginLeft: "10px",
                  position: "relative",
                  top: "-3px",
                }}
                onClick={() => setShowCustomStyleOptions(!showCustomStyleOptions)}
              >
                <SettingOutlined />
              </span>
            </Col>
          </Row>
          
          {showCustomStyleOptions && (
            <UITransition>
              <Row gutter={[8, 8]} style={{ marginBottom: "10px" }}>
                <Col span={24}>
                  <UICard>
                    <Row gutter={[8, 8]}>
                      <Col>
                        <UITitle level={5} color={BaseColor}>
                          Personalizza pagina
                        </UITitle>
                      </Col>
                    </Row>
                    <Row gutter={[8, 8]}>
                      <Col span={8}>
                        <UIText>Livello di zoom</UIText>
                        <Slider
                          min={0.7}
                          max={1}
                          onChange={(value: number) => setZoomLevel(value)}
                          value={typeof zoomLevel === "number" ? zoomLevel : 0}
                          step={0.1}
                          tooltipVisible={false}
                          marks={{
                            0.7: "70%",
                            0.8: "80%",
                            0.9: "90%",
                            1: "100%",
                          }}
                        />
                      </Col>
                    </Row>
                  </UICard>
                </Col>
              </Row>
            </UITransition>
          )}

          <CustomCard>
            <Row  justify="end" style={{ marginBottom: "15px" }}>
              {canAddTemporaryShift ? 
                <UIButton type="primary" onClick={() => setShowDrawerCreateTemporaryShift(true)}>
                  + Nuovo Evento
                </UIButton> 
                : <></>
              }
            </Row>
            <Row gutter={[16,16]}>
              <Col span={24}>
                <UITable
                  size="middle"
                  data-cy="events-list-table"
                  loading={eventsListLoading}
                  columns={eventsListColumns}
                  dataSource={eventsList?.data}
                  onChange={handleTableChange}
                  pagination={{
                    ...eventsList?.pagination,
                  }}
                />
              </Col>
            </Row>
          </CustomCard>

          <DrawerCreateTurniOccasionali 
            visible={showDrawerCreateTemporaryShift} 
            onClose={handleOnCloseDrawerCreateTurniOccasionali}   
            onSave={onSaveDrawerCreateTurniOccasionali}     
            isLoading={isCreateLoading}  
          />

        </CustomStyleProvider>
      </UITransition>


    </>
  );
}

const CustomCard = styled(UICard)`
  .ant-card-body {
    padding: 10px;
  }
`;